"use client";

import { useRef, useState, type KeyboardEvent } from "react";
import cls from "./SupportRoadmap.module.css";

const months = [
  {
    label: "Fix & measure",
    title: "Build a better starting point",
    steps: [
      { work: "Audit & first redesign updates", impact: "Make your offer clearer on key pages." },
      { work: "GA4 & form checks", impact: "See which visits turn into inquiries." },
      { work: "Content & technical SEO fixes", impact: "Help visitors and search engines navigate your site." },
    ],
    outcome: "Clearer pages. Working forms. A measurement baseline.",
  },
  {
    label: "Publish & reach",
    title: "Give people more ways to find you",
    steps: [
      { work: "SEO blog posts & page updates", impact: "Answer the questions your buyers search for." },
      { work: "Reddit mentions & monthly backlinks", impact: "Introduce your business to relevant audiences." },
      { work: "Fresh projects, copy & visuals", impact: "Show visitors what your business offers today." },
    ],
    outcome: "Useful new content and more routes to your website.",
  },
  {
    label: "Review & improve",
    title: "Let the data guide your next steps",
    steps: [
      { work: "GA4 & Search Console review", impact: "Find pages gaining attention or losing visitors." },
      { work: "Layout & call-to-action improvements", impact: "Make the next step towards an inquiry easier." },
      { work: "Next content & SEO priorities", impact: "Focus the next month on the opportunities in your data." },
    ],
    outcome: "A measured plan for improving visibility and inquiries.",
  },
];

export function SupportRoadmap() {
  const [activeMonth, setActiveMonth] = useState(0);
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number;

    switch (event.key) {
      case "ArrowRight":
        nextIndex = (index + 1) % months.length;
        break;
      case "ArrowLeft":
        nextIndex = (index + months.length - 1) % months.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = months.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    setActiveMonth(nextIndex);
    tabs.current[nextIndex]?.focus();
  };

  return (
    <section className={cls.timeline} aria-labelledby="support-roadmap-heading">
      <div className={cls.header}>
        <div>
          <p className={cls.eyebrow}>From upkeep to improvement</p>
          <h2 className={cls.title} id="support-roadmap-heading">Your first 90 days</h2>
        </div>
        <span className={cls.planBadge}>Example plan</span>
      </div>

      <div className={cls.tabs} role="tablist" aria-label="Monthly website support plan">
        {months.map((month, index) => (
          <button
            className={cls.tab}
            key={month.label}
            ref={(element) => { tabs.current[index] = element; }}
            type="button"
            role="tab"
            id={`support-month-tab-${index + 1}`}
            aria-controls={`support-month-panel-${index + 1}`}
            aria-selected={activeMonth === index}
            tabIndex={activeMonth === index ? 0 : -1}
            onClick={() => setActiveMonth(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span className={cls.monthNumber} aria-hidden="true">0{index + 1}</span>
            <span className={cls.monthLabel}>Month {index + 1}</span>
            <span className={cls.monthFocus}>{month.label}</span>
          </button>
        ))}
      </div>

      {months.map((month, index) => (
        <div
          className={cls.panel}
          key={month.label}
          id={`support-month-panel-${index + 1}`}
          role="tabpanel"
          aria-labelledby={`support-month-tab-${index + 1}`}
          tabIndex={0}
          hidden={activeMonth !== index}
        >
          <h3 className={cls.panelTitle}>{month.title}</h3>
          <div className={cls.columnLabels} aria-hidden="true">
            <span>What we do</span><span>Why it matters</span>
          </div>
          <dl className={cls.steps}>
            {month.steps.map((step) => (
              <div className={cls.step} key={step.work}>
                <dt>{step.work}</dt>
                <dd><span aria-hidden="true">↗</span>{step.impact}</dd>
              </div>
            ))}
          </dl>
          <div className={cls.outcome}>
            <span>What you build towards</span>
            <p>{month.outcome}</p>
          </div>
        </div>
      ))}

      <div className={cls.recurring}>
        <span className={cls.recurringLabel}><span aria-hidden="true">↻</span> Every month</span>
        <p>Hosting, backups, security updates, form checks & ongoing support.</p>
      </div>
      <p className={cls.note}>Example roadmap: priorities and volume are agreed for your site. SEO and promotion continue as the plan develops.</p>
    </section>
  );
}
